// Catalog.ID Client — "Keys on this device" picker (selection logic)
// ============================================================================
// device-setup-restructure §8, whitepaper §3.4.7. Every decision the picker
// makes lives here, DOM-free: which rows exist, which are ticked, which are
// locked, which are new, and what manifest comes out. `device-setup.ts` paints
// what `rows()` returns and calls `setChosen` on a change — nothing more.
//
// The catalogue (`state/device-key-selection.ts`) is the source of the rows
// themselves, so a key id added or deleted there moves this picker without a
// line changing here. The picker adds three things the catalogue cannot know:
// the account's own bag (for the "Other" group and the "new" marker), this
// device's stored manifest, and the member's clicks.

import {
    DEVICE_KEY_CATALOGUE, matchesDeviceKeyFamily,
    catalogueRowForKeyId, type DeviceKeyGroup, type DeviceKeyManifest,
} from '../state/device-key-selection';
import { isDeviceStorableKeyId } from '../crypto/keybag';

/** One line of the picker, ready to paint. */
export interface DeviceKeyPickerRow {
    /** What this row writes into the manifest: a catalogue key id, a family
     *  glob (`agent:*:agent_signing_key`), or the raw bag id of an "Other"
     *  row. Unique across `rows()`. */
    id: string;
    /** What the member reads. The raw id for an "Other" row — there is nothing
     *  truthful to call a key this build has never heard of. */
    label: string;
    group: DeviceKeyGroup;
    /** One sentence saying what the device loses by declining it. */
    description: string;
    /** Ticked and DISABLED: the three Core ids are constants, not a choice
     *  (device-setup-restructure §1 item 10, §12). */
    mandatory: boolean;
    /** Ticked right now. */
    chosen: boolean;
    /** Show the "new" tag: this device has a manifest, that manifest never
     *  decided this row, and the account actually holds the key (§8). A device
     *  with no manifest — registration — marks nothing, because every row is
     *  new to it and a screen of "new" tags says nothing. */
    isNew: boolean;
}

export interface DeviceKeyPickerInput {
    /** Every key id the account is known to hold: the decrypted bag's entries,
     *  and/or the server's key list. Order and duplicates do not matter. Used
     *  for the "Other" group and for the "new" marker — never to hide a
     *  catalogue row, because the wizard's device step runs BEFORE finalize
     *  mints `xrpl_master` and `derive_root` (§8). */
    presentKeyIds?: readonly string[];
    /** This device's stored manifest, or null when it has none (registration,
     *  or a device set up before the picker shipped). */
    manifest?: DeviceKeyManifest | null;
}

export interface DeviceKeyPicker {
    /** The rows to paint, in display order: the catalogue, then "Other". */
    rows(): DeviceKeyPickerRow[];
    /** Is the row with this id ticked? False for an id no row knows. */
    isChosen(id: string): boolean;
    /** Tick or untick a row. A mandatory row and an unknown id are both no-ops
     *  — the DOM disables the first and never renders the second, and this is
     *  the backstop that makes that true whatever the DOM does. */
    setChosen(id: string, chosen: boolean): void;
    /** The manifest this selection means (§7.1). Every row lands in exactly
     *  one list, mandatory ids always in `chosen`. */
    toManifest(): DeviceKeyManifest;
}

/** An "Other" row's description. Deliberately does not guess what the key is
 *  for: this build has no row for it, so it has nothing to say about it. */
const OTHER_ROW_DESCRIPTION =
    'A key this version of Catalog.ID has no name for. Leave it on unless you know otherwise.';

interface RowSpec {
    id: string;
    label: string;
    group: DeviceKeyGroup;
    description: string;
    mandatory: boolean;
    /** The family glob this row governs, when it governs one. */
    family?: string;
}

/**
 * Build the picker for one device.
 *
 * Defaults, in the order they apply to a row (§8, §1 item 14):
 *  1. mandatory → ticked, locked;
 *  2. no stored manifest → ticked (everything on, today's behaviour);
 *  3. declined by the stored manifest → unticked;
 *  4. chosen by it → ticked;
 *  5. undecided → ticked, and marked NEW when the account holds the key.
 */
export function createDeviceKeyPicker(input: DeviceKeyPickerInput = {}): DeviceKeyPicker {
    const manifest = input.manifest ?? null;
    const present = new Set(input.presentKeyIds ?? []);
    const specs = buildRows(present);
    const chosen = new Map<string, boolean>();
    const isNew = new Map<string, boolean>();

    for (const spec of specs) {
        chosen.set(spec.id, spec.mandatory || !listedFor(manifest?.declined, spec));
        isNew.set(spec.id, manifest !== null
            && !listedFor(manifest.chosen, spec)
            && !listedFor(manifest.declined, spec)
            && heldByAccount(spec, present));
    }

    return {
        rows(): DeviceKeyPickerRow[] {
            return specs.map(spec => ({
                id: spec.id,
                label: spec.label,
                group: spec.group,
                description: spec.description,
                mandatory: spec.mandatory,
                chosen: chosen.get(spec.id) === true,
                isNew: isNew.get(spec.id) === true,
            }));
        },
        isChosen(id: string): boolean {
            return chosen.get(id) === true;
        },
        setChosen(id: string, want: boolean): void {
            const spec = specs.find(s => s.id === id);
            if (!spec || spec.mandatory) return;
            chosen.set(id, want);
        },
        toManifest(): DeviceKeyManifest {
            const picked: string[] = [];
            const refused: string[] = [];
            for (const spec of specs) {
                (chosen.get(spec.id) === true ? picked : refused).push(spec.id);
            }
            return { v: 1, chosen: picked, declined: refused };
        },
    };
}

/** The catalogue in display order, then one "Other" row per device-storable bag
 *  id no catalogue row covers, sorted so the screen is stable between paints. */
function buildRows(present: ReadonlySet<string>): RowSpec[] {
    const specs: RowSpec[] = DEVICE_KEY_CATALOGUE.map(row => ({
        id: row.keyId ?? row.family!,
        label: row.label,
        group: row.group,
        description: row.description,
        mandatory: row.mandatory,
        family: row.family,
    }));
    const unknown = Array.from(present)
        // `recovery_kit` reaches no device at all (§16.5) — not a row, ever.
        .filter(keyId => isDeviceStorableKeyId(keyId) && catalogueRowForKeyId(keyId) === null)
        .sort();
    for (const keyId of unknown) {
        specs.push({
            id: keyId, label: keyId, group: 'Other',
            description: OTHER_ROW_DESCRIPTION, mandatory: false,
        });
    }
    return specs;
}

/** Does `list` decide this row? An exact id, or — for the agent row, which is
 *  ONE toggle — any concrete member of its family, so a manifest naming a
 *  single agent key still counts as a decision about the row. */
function listedFor(list: readonly string[] | undefined, spec: RowSpec): boolean {
    if (!list) return false;
    for (const entry of list) {
        if (entry === spec.id) return true;
        if (spec.family !== undefined && matchesDeviceKeyFamily(spec.family, entry)) return true;
    }
    return false;
}

/** Does the account hold a key this row governs? */
function heldByAccount(spec: RowSpec, present: ReadonlySet<string>): boolean {
    if (spec.family !== undefined) {
        for (const keyId of present) if (matchesDeviceKeyFamily(spec.family, keyId)) return true;
        return false;
    }
    return present.has(spec.id);
}
